import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { JsonLd } from '../components/SEO/JsonLd';
import { buildOrganizationSchema, buildTNEAFAQSchema, buildTNEAScheduleSchema } from '../utils/seo';
import { CUTOFF_END_YEAR } from '../constants/years';
import { SITE_CONFIG } from '../config/siteConfig';
import { FaSearch, FaBalanceScale, FaUniversity, FaMapMarkedAlt, FaArrowRight, FaCheckCircle } from 'react-icons/fa';

const Home = () => {
  const features = [
    {
      icon: <FaUniversity className="text-3xl text-blue-600" />,
      title: 'College Search',
      description: 'Filter 420+ colleges by district, type, NAAC grade, hostel & fees.',
      link: '/colleges',
      bg: 'bg-blue-50',
      border: 'border-blue-100',
    },
    {
      icon: <FaBalanceScale className="text-3xl text-indigo-600" />,
      title: 'Compare Cutoffs',
      description: 'See closing ranks side-by-side across colleges, communities & years.',
      link: '/cutoffs',
      bg: 'bg-indigo-50',
      border: 'border-indigo-100',
    },
    {
      icon: <FaSearch className="text-3xl text-violet-600" />,
      title: 'College Predictor',
      description: 'Enter your rank and community — instantly see which colleges you qualify for.',
      link: '/predictor',
      bg: 'bg-violet-50',
      border: 'border-violet-100',
    },
    {
      icon: <FaMapMarkedAlt className="text-3xl text-sky-600" />,
      title: 'District Rankings',
      description: 'Find the best colleges near your district ranked by last admitted rank.',
      link: '/district-rankings',
      bg: 'bg-sky-50',
      border: 'border-sky-100',
    },
  ];

  const stats = [
    { value: '476+', label: 'Engineering Colleges', sub: 'across Tamil Nadu' },
    { value: '38', label: 'Districts Covered', sub: 'every district' },
    { value: '6 Years', label: 'Cutoff History', sub: '2020 – 2025' },
    { value: 'Free', label: 'No Login Needed', sub: 'open for everyone' },
  ];

  const steps = [
    { step: '1', title: 'Enter your rank & community', desc: 'Use the College Predictor with your TNEA rank.' },
    { step: '2', title: 'Shortlist colleges', desc: 'Filter by district, college type, branch, or hostel availability.' },
    { step: '3', title: 'Compare & decide', desc: 'Check closing ranks, fees, NAAC grade and make a confident choice.' },
  ];

  return (
    <>
      <Helmet>
        <title>{`ChooseMyCollege — TNEA ${SITE_CONFIG.year} College Predictor Tamil Nadu`}</title>
        <meta name="description" content={`Free TNEA ${SITE_CONFIG.year} college predictor for Tamil Nadu. Enter rank or cutoff, compare 476 colleges by district, community and NAAC grade based on ${CUTOFF_END_YEAR} cutoff data. Free choice list builder.`} />
        <meta name="keywords" content={`TNEA ${SITE_CONFIG.year}, TNEA college predictor, Tamil Nadu engineering admissions, TNEA cutoff ${CUTOFF_END_YEAR}, Anna University colleges, engineering college Tamil Nadu, TNEA rank college list`} />
        <meta property="og:title" content={`ChooseMyCollege — Free TNEA ${SITE_CONFIG.year} College Predictor & Cutoff Guide`} />
        <meta property="og:description" content={`Free TNEA ${SITE_CONFIG.year} guidance. Enter your mark or rank and instantly find Tamil Nadu engineering colleges you qualify for.`} />
        <meta property="og:url" content="https://choosemycollege.in/" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://choosemycollege.in/" />
      </Helmet>

      <JsonLd data={[buildOrganizationSchema(), buildTNEAFAQSchema(), ...buildTNEAScheduleSchema()]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white rounded-2xl p-8 md:p-14 mb-10">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block bg-white/15 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            TNEA Counselling Guide
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4 leading-tight">
            Know Your College Chances <br className="hidden md:block" />
            Before Counselling Day
          </h1>
          <p className="text-base md:text-lg text-blue-100 mb-8 max-w-xl mx-auto">
            Enter your rank, pick your community, and instantly see which engineering colleges you can get — with real cutoff data from past years.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/predictor"
              className="bg-white text-blue-700 font-bold px-8 py-3.5 rounded-xl hover:bg-blue-50 transition-colors shadow-lg flex items-center justify-center gap-2"
            >
              Predict My College <FaArrowRight className="text-sm" />
            </Link>
            <Link
              to="/colleges"
              className="bg-white/15 border-2 border-white/40 text-white font-bold px-8 py-3.5 rounded-xl hover:bg-white/25 transition-colors flex items-center justify-center gap-2"
            >
              Browse Colleges
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 text-center">
            <div className="text-2xl md:text-3xl font-extrabold text-blue-600 mb-1">{stat.value}</div>
            <div className="text-sm font-semibold text-gray-700">{stat.label}</div>
            <div className="text-xs text-gray-400 mt-0.5">{stat.sub}</div>
          </div>
        ))}
      </section>

      {/* TNEA 2026 Schedule */}
      {(() => {
        const today = new Date();
        const events = [
          { label: 'Registration & Application Filing',    start: '2026-05-03', end: '2026-05-03',  display: '03 May 2026' },
          { label: 'Last Date — Online Registration',      start: '2026-06-02', end: '2026-06-02',  display: '02 Jun 2026' },
          { label: 'Last Date — Document Upload',          start: '2026-06-06', end: '2026-06-06',  display: '06 Jun 2026' },
          { label: 'Assigning Random Number',              start: '2026-06-05', end: '2026-06-05',  display: '05 Jun 2026' },
          { label: 'Certificate Verification (Online)',    start: '2026-06-08', end: '2026-06-20',  display: '08 – 20 Jun 2026' },
          { label: 'Publication of Rank List',             start: '2026-06-29', end: '2026-06-29',  display: '29 Jun 2026' },
          { label: 'Grievances Redressal',                 start: '2026-06-30', end: '2026-07-04',  display: '30 Jun – 04 Jul 2026' },
          { label: 'Special Reservation Counselling',      start: '2026-07-13', end: '2026-07-14', display: '13 – 14 Jul 2026' },
          { label: 'General Counselling (Choice Filling)', start: '2026-07-20', end: '2026-08-30', display: '20 Jul – 30 Aug 2026' },
          { label: 'Supplementary Counselling',            start: null, end: null, display: 'TBD' },
          { label: 'End of Counselling',                   start: null, end: null, display: 'TBD' },
        ];

        const getStatus = (start: string | null, end: string | null) => {
          if (!start) return 'tbd';
          const endDate = new Date(end || start);
          const startDate = new Date(start);
          if (endDate < today) return 'done';
          if (startDate <= today) return 'active';
          return 'upcoming';
        };

        const statusStyle = {
          done:     { dot: 'bg-green-500', date: 'text-green-700 bg-green-50 border-green-200', text: 'text-gray-400 line-through', chip: 'bg-green-100 text-green-700' },
          active:   { dot: 'bg-blue-500 animate-pulse', date: 'text-blue-700 bg-blue-50 border-blue-200', text: 'text-gray-800 font-semibold', chip: 'bg-blue-100 text-blue-700' },
          upcoming: { dot: 'bg-orange-400', date: 'text-orange-700 bg-orange-50 border-orange-200', text: 'text-gray-700', chip: 'bg-orange-100 text-orange-700' },
          tbd:      { dot: 'bg-gray-300', date: 'text-gray-400 bg-gray-50 border-gray-200', text: 'text-gray-400', chip: 'bg-gray-100 text-gray-400' },
        };

        return (
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 mb-10">
            {/* Header */}
            <div className="mb-4 text-center">
              <h2 className="text-lg md:text-xl font-bold text-gray-800">TNEA 2026 Counselling Schedule</h2>
              <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-gray-400">Tentative · Source: DOTE</span>
                <span className="text-gray-300">·</span>
                <a
                  href="https://static.tneaonline.org/docs/TNEA_Tentative_Schedule_2026.pdf?t=1783342947337"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-500 hover:text-blue-700 hover:underline"
                >
                  View official PDF ↗
                </a>
              </div>
            </div>

            {/* Table */}
            <div className="max-w-2xl mx-auto overflow-hidden rounded-xl border border-gray-100">
              {events.map((evt, i) => {
                const status = getStatus(evt.start, evt.end);
                const s = statusStyle[status];
                const rowBg = i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60';
                const chipLabel = status === 'done' ? 'Done' : status === 'active' ? 'Now' : status === 'upcoming' ? 'Upcoming' : 'TBD';
                return (
                  <div key={i} className={`flex items-start gap-2.5 px-4 py-3 border-b border-gray-100 last:border-0 ${rowBg}`}>
                    {/* Dot aligned to first text line */}
                    <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${s.dot}`} />
                    <div className="flex-1 min-w-0">
                      {/* Line 1: date badge + status chip */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`flex-shrink-0 text-[11px] font-semibold border rounded px-2 py-0.5 ${s.date}`}>
                          {evt.display}
                        </span>
                        <span className={`flex-shrink-0 text-[10px] font-semibold px-2.5 py-0.5 rounded-full ${s.chip}`}>
                          {chipLabel}
                        </span>
                      </div>
                      {/* Line 2: event label */}
                      <p className={`text-sm leading-snug ${s.text}`}>{evt.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-[11px] text-gray-400 mt-3 text-center">
              * Counselling dates (G–L) will be updated after AICTE approval.
            </p>
          </section>
        );
      })()}

      {/* Features */}
      <section className="mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 text-gray-800">
          Everything You Need to Decide
        </h2>
        <p className="text-center text-gray-500 mb-8 text-sm md:text-base">
          Built for 12th students and parents navigating TNEA counselling
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <Link
              key={i}
              to={f.link}
              className={`group rounded-xl border ${f.border} ${f.bg} p-6 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5`}
            >
              <div className="mb-4">{f.icon}</div>
              <h3 className="text-base font-bold text-gray-800 mb-2 group-hover:text-blue-700 transition-colors">
                {f.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
              <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                Open <FaArrowRight className="text-[10px]" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 md:p-10 mb-10">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-8 text-center">
          How to Use This in 3 Steps
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-lg flex items-center justify-center flex-shrink-0">
                {s.step}
              </div>
              <div>
                <div className="font-semibold text-gray-800 mb-1">{s.title}</div>
                <div className="text-sm text-gray-500">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
            {['No login required', 'Free to use', 'Real TNEA data'].map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <FaCheckCircle className="text-green-500" /> {t}
              </span>
            ))}
          </div>
          <Link
            to="/predictor"
            className="bg-blue-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            Get Started <FaArrowRight className="text-sm" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Home;
